// App-wide passive FullStory tracker for quoting-ui button clicks and field interactions.
import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgKLoggerService } from '@kin/ngk-logger';
import { WA_WINDOW } from '@ng-web-apis/common';
import { fromEvent } from 'rxjs';

import { FullstoryService } from 'app/shared/services/fullstory/fullstory.service';
import { PassiveTrackingDom } from 'app/shared/services/fullstory/quote-passive-tracking/quote-passive-tracking.dom';
import { PassiveTrackingIdentifiers } from 'app/shared/services/fullstory/quote-passive-tracking/quote-passive-tracking.identifiers';
import { isQuoteTrackingRoute } from 'app/shared/services/fullstory/quote-passive-tracking/quote-passive-tracking.rules';
import { PassiveTrackingSelectors } from 'app/shared/services/fullstory/quote-passive-tracking/quote-passive-tracking.selectors';
import { FeatureFlagSignalsService } from 'app/shared/services/splitIO/feature-flag.service';
import { matches } from 'app/shared/utils/dom/dom-helpers';
import { normalizeText } from 'app/shared/utils/text/text-helpers';

type QuoteTrackingActionType = 'button-click' | 'field-interaction';

type RecentPointerInteraction = {
  element: HTMLElement;
  timestamp: number;
};

@Injectable({
  providedIn: 'root',
})
export class QuotePassiveTrackingService {
  private static readonly RECENT_POINTER_WINDOW_MS = 250;
  private static readonly HEADER_PAGE_KEY = 'quoting-ui-nav';
  private static readonly PAGE_KEY_PREFIX = 'quoting-ui';

  private readonly logger = inject(NgKLoggerService);
  private readonly window = inject(WA_WINDOW);
  private readonly fullstoryService = inject(FullstoryService);
  private readonly featureFlagSignalsService = inject(FeatureFlagSignalsService);
  private readonly destroyRef = inject(DestroyRef);

  private recentPointerInteraction: RecentPointerInteraction | null = null;

  constructor() {
    const doc = this.window.document;

    fromEvent<Event>(doc, 'pointerdown', { capture: true })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => this.handlePointerDown(event));

    fromEvent<Event>(doc, 'focusin', { capture: true })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => this.handleFocusIn(event));

    fromEvent<Event>(doc, 'click', { capture: true })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => this.handleClick(event));
  }

  private handlePointerDown(event: Event): void {
    try {
      this.trackFieldInteractionFromEvent(event, 'pointer');
    } catch (error) {
      this.logTrackingError('pointerdown', error);
    }
  }

  private handleFocusIn(event: Event): void {
    try {
      this.trackFieldInteractionFromEvent(event, 'focus');
    } catch (error) {
      this.logTrackingError('focusin', error);
    }
  }

  private handleClick(event: Event): void {
    try {
      const path = this.getEventPathElements(event);
      if (!this.shouldTrackClick(path)) return;
      if (this.isRedundantNestedRadioClick(path)) return;

      const button = PassiveTrackingSelectors.findTrackableButton(path, this.window.location.origin);
      if (!button || PassiveTrackingSelectors.isIgnoredButton(button, path)) return;

      const identifier = PassiveTrackingIdentifiers.getButtonIdentifier(button);
      if (!identifier) return;

      this.trackEvent(this.getPageKeyForInteraction(path), 'button-click', identifier, {
        page_path: this.window.location.pathname,
        source: 'passive',
        tag_name: button.tagName.toLowerCase(),
        text: normalizeText(button.textContent),
      });
    } catch (error) {
      this.logTrackingError('click', error);
    }
  }

  private shouldTrackClick(path: HTMLElement[]): boolean {
    if (path.some((element) => !!element.closest('app-header'))) {
      return true;
    }

    return isQuoteTrackingRoute(this.window.location.pathname);
  }

  private getPageKey(): string {
    const path = this.window.location.pathname
      .split('/')
      .filter(Boolean)
      .map((segment) => (/^\d+$/.test(segment) ? ':id' : segment))
      .join('-');

    return path ? `${QuotePassiveTrackingService.PAGE_KEY_PREFIX}-${path}` : QuotePassiveTrackingService.PAGE_KEY_PREFIX;
  }

  private getPageKeyForInteraction(eventPath: HTMLElement[]): string {
    if (eventPath.some((element) => !!element.closest('app-header'))) {
      return QuotePassiveTrackingService.HEADER_PAGE_KEY;
    }

    return this.getPageKey();
  }

  private getEventPathElements(event: Event): HTMLElement[] {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [event.target];
    return path.filter((value): value is HTMLElement => value instanceof HTMLElement);
  }

  private trackFieldInteractionFromEvent(event: Event, source: 'pointer' | 'focus'): void {
    if (!isQuoteTrackingRoute(this.window.location.pathname)) return;

    const path = this.getEventPathElements(event);
    const field = PassiveTrackingSelectors.findTrackableField(path);
    if (!field || PassiveTrackingSelectors.isIgnoredField(field)) return;
    if (PassiveTrackingSelectors.isIgnoredFieldInteraction(field, path)) return;
    if (source === 'focus' && this.wasRecentlyTrackedByPointer(field)) return;

    const identifier = PassiveTrackingIdentifiers.getFieldIdentifier(field);
    if (!identifier) return;

    this.trackEvent(this.getPageKey(), 'field-interaction', identifier, {
      page_path: this.window.location.pathname,
      source: 'passive',
      field_type: field.tagName.toLowerCase(),
      identifier,
    });

    if (source === 'pointer') {
      this.rememberPointerInteraction(field);
    }
  }

  private wasRecentlyTrackedByPointer(field: HTMLElement): boolean {
    if (!this.recentPointerInteraction) return false;

    const elapsed = Date.now() - this.recentPointerInteraction.timestamp;
    if (elapsed > QuotePassiveTrackingService.RECENT_POINTER_WINDOW_MS) {
      this.recentPointerInteraction = null;
      return false;
    }

    return this.recentPointerInteraction.element === field;
  }

  private rememberPointerInteraction(field: HTMLElement): void {
    this.recentPointerInteraction = {
      element: field,
      timestamp: Date.now(),
    };
  }

  private isRedundantNestedRadioClick(path: HTMLElement[]): boolean {
    return path[0] instanceof HTMLInputElement && path[0].type === 'radio' && path.some((element) => matches(element, PassiveTrackingDom.RADIO_CARD_SELECTOR));
  }

  private trackEvent(page: string, actionType: QuoteTrackingActionType, identifier: string, metadata: Record<string, unknown> = {}): void {
    if (!this.featureFlagSignalsService.isPassiveFullstoryTrackingEnabled()) return;

    this.fullstoryService.trackCustomEvent(`${page} | ${actionType} | ${identifier}`, metadata);
  }

  private logTrackingError(stage: string, error: unknown): void {
    this.logger.error({
      message: `Quote passive tracking failed during ${stage}: ${error}`,
      context: 'QuotePassiveTrackingService',
      priority: 'P4',
    });
  }
}
